import { useEffect, useMemo, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { BusinessAction } from "actions";
import { useHasGlobalBusinessSearchPermission } from "hooks";
import { getBusinesses, getProperties } from "selectors";
import {
  ConnectedBusinessSearchResult,
  connectedToMatchCandidate,
  getBusinessPics,
  MatchCandidate,
} from "selectors/temporaryBusinesses";

const MIN_SEARCH_LENGTH = 3;

// The server reports which of its fields contain any word of the search term
// (eg a name word inside a PIC), not what matches the temporary Business - so
// only keep PIC matches that actually match, and the name.
function verifyMatchedFields(
  candidate: MatchCandidate,
  temporaryPics: string[],
): MatchCandidate {
  const matchedFields = candidate.matchedFields.filter(
    field =>
      field === "name" ||
      (field === "pic" &&
        candidate.pics.some(pic => temporaryPics.includes(pic))),
  );
  return { ...candidate, matchedFields };
}

const EMPTY_MATCHES: MatchCandidate[] = [];
const EMPTY_RESULTS: ConnectedBusinessSearchResult[] = [];

type Dispatch = (action: unknown) => void;
type PropertyLookup = Parameters<typeof getBusinessPics>[1];

// Connected Businesses aren't in state, so cache server searches for the
// session.
const resultsBySearchKey = new Map<
  string,
  Promise<ConnectedBusinessSearchResult[]>
>();

function searchConnected(
  dispatch: Dispatch,
  term: string,
): Promise<ConnectedBusinessSearchResult[]> {
  return new Promise((resolve, reject) => {
    dispatch(
      BusinessAction.getGlobalBusinessesForAutoComplete(term, {
        onSuccess: response => resolve(response?.results || []),
        onError: reject,
      }),
    );
  });
}

// A failed search isn't kept, so it's tried again next time.
function cachedSearch(
  searchKey: string,
  search: () => Promise<ConnectedBusinessSearchResult[]>,
): Promise<ConnectedBusinessSearchResult[]> {
  if (!resultsBySearchKey.has(searchKey)) {
    resultsBySearchKey.set(
      searchKey,
      search().catch(() => {
        resultsBySearchKey.delete(searchKey);
        return EMPTY_RESULTS;
      }),
    );
  }
  return resultsBySearchKey.get(searchKey);
}

// Searched by the temporary Business's first PIC and name - all it has.
function getSearchTerms(
  temporaryBusiness: Business,
  propertyByIdLookup: PropertyLookup,
): string[] {
  return [
    getBusinessPics(temporaryBusiness, propertyByIdLookup)[0],
    temporaryBusiness.name,
  ]
    .map(term => term?.trim())
    .filter(term => term && term.length >= MIN_SEARCH_LENGTH);
}

function loadConnectedResults(
  dispatch: Dispatch,
  temporaryBusiness: Business,
  propertyByIdLookup: PropertyLookup,
): Promise<ConnectedBusinessSearchResult[]> {
  const terms = getSearchTerms(temporaryBusiness, propertyByIdLookup);
  return cachedSearch(terms.join("|"), () =>
    Promise.all(terms.map(term => searchConnected(dispatch, term))).then(
      responses => {
        const byId = new Map<string, ConnectedBusinessSearchResult>();
        responses.flat().forEach(result => {
          const existing = byId.get(result.id);
          byId.set(result.id, {
            ...result,
            matched_fields: Array.from(
              new Set([
                ...(existing?.matched_fields || []),
                ...(result.matched_fields || []),
              ]),
            ),
          });
        });
        return Array.from(byId.values());
      },
    ),
  );
}

// Connected results per temporary Business id - undefined while searching.
// Ones already added to the user's Businesses are left out (they're matched
// as existing Businesses).
export function useConnectedBusinessMatchesById(
  temporaryBusinesses: Business[],
): Record<string, MatchCandidate[] | undefined> {
  const dispatch = useDispatch();
  const businesses: Record<string, Business> = useSelector(getBusinesses);
  const propertyByIdLookup = useSelector(getProperties);
  const hasPermission = useHasGlobalBusinessSearchPermission();

  // Keyed on the ids, so callers needn't keep the array stable.
  const temporaryBusinessesRef = useRef(temporaryBusinesses);
  temporaryBusinessesRef.current = temporaryBusinesses;
  const temporaryBusinessIds = temporaryBusinesses
    .map(business => business.id)
    .join(",");

  const [resultsById, setResultsById] = useState<
    Record<string, ConnectedBusinessSearchResult[]>
  >({});

  useEffect(() => {
    if (!hasPermission) {
      return undefined;
    }
    let isCurrent = true;
    temporaryBusinessesRef.current.forEach(temporaryBusiness => {
      loadConnectedResults(
        dispatch,
        temporaryBusiness,
        propertyByIdLookup,
      ).then(results => {
        if (isCurrent) {
          setResultsById(current =>
            current[temporaryBusiness.id] === results
              ? current
              : { ...current, [temporaryBusiness.id]: results },
          );
        }
      });
    });
    return () => {
      isCurrent = false;
    };
  }, [dispatch, hasPermission, temporaryBusinessIds, propertyByIdLookup]);

  return useMemo(
    () =>
      hasPermission
        ? Object.fromEntries(
            Object.entries(resultsById).map(([id, results]) => [
              id,
              results
                .filter(result => !businesses[result.id])
                .map(result => {
                  const temporaryBusiness = businesses[id];
                  const candidate = connectedToMatchCandidate(result);
                  return temporaryBusiness
                    ? verifyMatchedFields(
                        candidate,
                        getBusinessPics(temporaryBusiness, propertyByIdLookup),
                      )
                    : candidate;
                })
                .filter(candidate => candidate.matchedFields.length)
                .sort(
                  (a, b) => b.matchedFields.length - a.matchedFields.length,
                ),
            ]),
          )
        : Object.fromEntries(
            (temporaryBusinessIds ? temporaryBusinessIds.split(",") : []).map(
              id => [id, EMPTY_MATCHES],
            ),
          ),
    [
      hasPermission,
      resultsById,
      businesses,
      temporaryBusinessIds,
      propertyByIdLookup,
    ],
  );
}

export function useConnectedBusinessMatches(
  temporaryBusiness: Business,
): MatchCandidate[] {
  return (
    useConnectedBusinessMatchesById([temporaryBusiness])[
      temporaryBusiness.id
    ] ?? EMPTY_MATCHES
  );
}

// Connected Businesses for a typed search, as match candidates.
export function useConnectedBusinessSearch(term: string): MatchCandidate[] {
  const dispatch = useDispatch();
  const businesses: Record<string, Business> = useSelector(getBusinesses);
  const hasPermission = useHasGlobalBusinessSearchPermission();
  const searchTerm = term.trim();
  const [results, setResults] =
    useState<ConnectedBusinessSearchResult[]>(EMPTY_RESULTS);

  useEffect(() => {
    if (!hasPermission || searchTerm.length < MIN_SEARCH_LENGTH) {
      setResults(EMPTY_RESULTS);
      return undefined;
    }
    let isCurrent = true;
    cachedSearch(searchTerm, () => searchConnected(dispatch, searchTerm)).then(
      response => {
        if (isCurrent) {
          setResults(response);
        }
      },
    );
    return () => {
      isCurrent = false;
    };
  }, [dispatch, hasPermission, searchTerm]);

  return useMemo(
    () =>
      results
        .filter(result => !businesses[result.id])
        .map(result => ({
          ...connectedToMatchCandidate(result),
          matchedFields: [],
        })),
    [results, businesses],
  );
}
